import { type BspReader, LUMP } from './reader';

/**
 * Struct decoders for the Source-1 BSP lumps Fall Line consumes. Layouts and
 * sizes follow the Valve Developer Community spec; only the fields we use are
 * pulled out. All readers return plain arrays so they unit-test headlessly.
 */

// ── Content + surface flags ─────────────────────────────────────────────────
export const CONTENTS_SOLID = 0x1;
export const CONTENTS_PLAYERCLIP = 0x10000;
/** A brush is part of the ride surface if it stops the player. */
export const RIDE_CONTENTS = CONTENTS_SOLID | CONTENTS_PLAYERCLIP;

/** texinfo flags that mark a face as a non-visible tool surface (skip visually). */
export const SURF_SKY = 0x0004;
export const SURF_SKY2D = 0x0002;
export const SURF_TRIGGER = 0x0040;
export const SURF_NODRAW = 0x0080;
export const SURF_HINT = 0x0100;
export const SURF_SKIP = 0x0200;
export const TOOL_SURF_MASK =
  SURF_SKY | SURF_SKY2D | SURF_TRIGGER | SURF_NODRAW | SURF_HINT | SURF_SKIP;

// ── Structs ─────────────────────────────────────────────────────────────────
export interface Plane {
  nx: number;
  ny: number;
  nz: number;
  dist: number;
}
export interface Brush {
  firstside: number;
  numsides: number;
  contents: number;
}
export interface BrushSide {
  planenum: number;
  bevel: number;
}
export interface Face {
  planenum: number;
  firstedge: number;
  numedges: number;
  texinfo: number;
  /** Index into the dispinfo lump, or -1 if this face isn't a displacement. */
  dispinfo: number;
}
export interface DispInfo {
  /** Source-space corner the displacement grid starts from (lower-left). */
  startPosition: Vec3;
  /** First index into the disp-vert lump for this displacement. */
  dispVertStart: number;
  /** Subdivision power (2–4); side vertices = 2^power + 1. */
  power: number;
}
export interface DispVert {
  /** Unit-ish offset direction (Source space). */
  vec: Vec3;
  /** Offset distance along `vec`. */
  dist: number;
}
export interface TexInfo {
  flags: number;
}
export interface Model {
  min: [number, number, number];
  max: [number, number, number];
  firstface: number;
  numfaces: number;
}
export type Vec3 = [number, number, number];

export function readPlanes(r: BspReader): Plane[] {
  return r.readStructs(LUMP.PLANES, 20, (o) => ({
    nx: r.view.getFloat32(o, true),
    ny: r.view.getFloat32(o + 4, true),
    nz: r.view.getFloat32(o + 8, true),
    dist: r.view.getFloat32(o + 12, true),
  }));
}

export function readBrushes(r: BspReader): Brush[] {
  return r.readStructs(LUMP.BRUSHES, 12, (o) => ({
    firstside: r.view.getInt32(o, true),
    numsides: r.view.getInt32(o + 4, true),
    contents: r.view.getInt32(o + 8, true),
  }));
}

export function readBrushSides(r: BspReader): BrushSide[] {
  return r.readStructs(LUMP.BRUSHSIDES, 8, (o) => ({
    planenum: r.view.getUint16(o, true),
    bevel: r.view.getUint16(o + 6, true),
  }));
}

export function readVertexes(r: BspReader): Vec3[] {
  return r.readStructs(LUMP.VERTEXES, 12, (o) => [
    r.view.getFloat32(o, true),
    r.view.getFloat32(o + 4, true),
    r.view.getFloat32(o + 8, true),
  ]);
}

/** dedge_t: two unsigned-short vertex indices. */
export function readEdges(r: BspReader): [number, number][] {
  return r.readStructs(LUMP.EDGES, 4, (o) => [
    r.view.getUint16(o, true),
    r.view.getUint16(o + 2, true),
  ]);
}

/** Signed indices into the edge array (sign = direction). */
export function readSurfedges(r: BspReader): number[] {
  return r.readStructs(LUMP.SURFEDGES, 4, (o) => r.view.getInt32(o, true));
}

export function readFaces(r: BspReader): Face[] {
  return r.readStructs(LUMP.FACES, 56, (o) => ({
    planenum: r.view.getUint16(o, true),
    firstedge: r.view.getInt32(o + 4, true),
    numedges: r.view.getInt16(o + 8, true),
    texinfo: r.view.getInt16(o + 10, true),
    dispinfo: r.view.getInt16(o + 12, true),
  }));
}

export function readDispInfo(r: BspReader): DispInfo[] {
  // ddispinfo_t is 176 bytes; we use startPosition, DispVertStart and power.
  return r.readStructs(LUMP.DISPINFO, 176, (o) => ({
    startPosition: [
      r.view.getFloat32(o, true),
      r.view.getFloat32(o + 4, true),
      r.view.getFloat32(o + 8, true),
    ],
    dispVertStart: r.view.getInt32(o + 12, true),
    power: r.view.getInt32(o + 20, true),
  }));
}

export function readDispVerts(r: BspReader): DispVert[] {
  // ddispvert_t: vec[3] (12) + dist (4) + alpha (4) = 20 bytes.
  return r.readStructs(LUMP.DISP_VERTS, 20, (o) => ({
    vec: [
      r.view.getFloat32(o, true),
      r.view.getFloat32(o + 4, true),
      r.view.getFloat32(o + 8, true),
    ],
    dist: r.view.getFloat32(o + 12, true),
  }));
}

export function readTexInfo(r: BspReader): TexInfo[] {
  // texinfo_t is 72 bytes; flags is an int at offset 64.
  return r.readStructs(LUMP.TEXINFO, 72, (o) => ({ flags: r.view.getInt32(o + 64, true) }));
}

export function readModels(r: BspReader): Model[] {
  // dmodel_t: mins[3] maxs[3] origin[3] headnode firstface numfaces = 48 bytes.
  return r.readStructs(LUMP.MODELS, 48, (o) => ({
    min: [
      r.view.getFloat32(o, true),
      r.view.getFloat32(o + 4, true),
      r.view.getFloat32(o + 8, true),
    ],
    max: [
      r.view.getFloat32(o + 12, true),
      r.view.getFloat32(o + 16, true),
      r.view.getFloat32(o + 20, true),
    ],
    firstface: r.view.getInt32(o + 36, true),
    numfaces: r.view.getInt32(o + 40, true),
  }));
}
